import { PlusIcon, TrashIcon, FilterIcon, EditIcon, CheckIcon, XIcon } from 'lucide-react';
import { Input, Button as AntButton, Modal, message, Spin } from 'antd';
import { TSmartVariableDefinition, TFilterGroup, TFilterView } from '@/types/TSmartCRM';
import { DynamicField } from '../DynamicField/DynamicField';
import { EFieldMode, EFilterOperator } from 'src/enums/ESmartCRM';
import { FIELD_TYPE_CONFIGS, FILTER_OPERATOR_LABELS } from 'src/constants/smartCRM';
import { useFilterBuilder } from 'src/Hooks/SmartCRM/useFilterBuilder';

interface TFilterBuilderProps {
	fields: TSmartVariableDefinition[];
	activeFilterView?: TFilterView | null;
	onFiltersChange?: (filters: TFilterGroup[]) => void;
	onApplyFilters?: (filters: TFilterGroup[]) => void;
	onSaveAsView?: (name: string, filters: any, viewId?: string) => void;
	onSwitchToCustomers?: () => void;
	visible?: boolean;
	isLoading?: boolean;
}

export const FilterBuilder = ({
	fields,
	activeFilterView,
	onFiltersChange,
	onSaveAsView,
	onSwitchToCustomers,
	visible = true,
	isLoading = false,
}: TFilterBuilderProps) => {
	const {
		filterGroups,
		showSaveModal,
		viewName,
		editingGroupId,
		editingGroupName,
		isEditingExistingView,
		hasValidFilters,
		setViewName,
		setEditingGroupId,
		setEditingGroupName,
		setShowSaveModal,
		addFilterGroup,
		removeGroup,
		addCondition,
		removeCondition,
		updateCondition,
		updateGroupName,
		handleSaveOrUpdate,
		handleNewViewSave,
	} = useFilterBuilder({
		fields,
		activeFilterView,
		onFiltersChange,
		onSaveAsView,
		onSwitchToCustomers,
	});

	const getAvailableOperators = (fieldKey: string) => {
		const field = fields.find((f) => f.internalKey === fieldKey);
		if (!field || !FIELD_TYPE_CONFIGS[field.fieldType]) {
			return [];
		}
		return FIELD_TYPE_CONFIGS[field.fieldType].operators;
	};

	const startEditingGroupName = (groupId: string, currentName: string) => {
		setEditingGroupId(groupId);
		setEditingGroupName(currentName);
	};

	const saveGroupName = (groupId: string) => {
		if (!editingGroupName.trim()) {
			message.error('Group name cannot be empty');
			return;
		}
		updateGroupName(groupId, editingGroupName.trim());
		setEditingGroupId(null);
		setEditingGroupName('');
	};

	const cancelEditingGroupName = () => {
		setEditingGroupId(null);
		setEditingGroupName('');
	};

	if (!visible) {
		return null;
	}

	const buttonText = isEditingExistingView ? 'Update' : 'Save & Apply';

	return (
		<>
			<div className="bg-white p-4 mb-6 shadow-sm border-t border-gray-200">
				<div className="space-y-4">
					<div className="flex justify-between items-center">
						<div>
							<div className="flex items-center gap-2 mb-1">
								<FilterIcon className="w-5 h-5 text-gray-600" />
								<h3 className="text-lg font-semibold text-gray-900 m-0">
									{isEditingExistingView
										? `Edit ${activeFilterView?.name}`
										: 'Find Specific Customers'}
								</h3>
							</div>
							<p className="text-sm text-gray-600 m-0">
								{isEditingExistingView
									? 'Modify the filters for this view'
									: "Build filters to find exactly the customers you're looking for"}
							</p>
						</div>
						{hasValidFilters && (
							<div className="flex gap-2">
								<button
									onClick={handleSaveOrUpdate}
									disabled={isLoading}
									className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
								>
									{isLoading ? (
										<>
											<Spin size="small" className="text-white" />
											{buttonText}
										</>
									) : (
										buttonText
									)}
								</button>
							</div>
						)}
					</div>

					{filterGroups.length === 0 ? (
						<div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-gray-50">
							<p className="text-gray-600 mb-3">
								No conditions yet. Click "Add Filter Group" to get started.
							</p>
							<button
								onClick={addFilterGroup}
								className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
							>
								<PlusIcon className="w-4 h-4" />
								Add Filter Group
							</button>
						</div>
					) : (
						<div className="space-y-4">
							<div className="max-h-96 overflow-y-auto space-y-4">
								{filterGroups.map((group) => (
									<div key={group.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
										<div className="flex justify-between items-center mb-4">
											<div className="flex items-center gap-2">
												{editingGroupId === group.id ? (
													<div className="flex items-center gap-2">
														<input
															type="text"
															value={editingGroupName}
															onChange={(e) => setEditingGroupName(e.target.value)}
															className="px-2 py-1 border border-gray-300 rounded text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
															onKeyDown={(e) => {
																if (e.key === 'Enter') {
																	saveGroupName(group.id);
																} else if (e.key === 'Escape') {
																	cancelEditingGroupName();
																}
															}}
															autoFocus
														/>
														<button
															onClick={() => saveGroupName(group.id)}
															className="inline-flex items-center justify-center w-6 h-6 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors"
														>
															<CheckIcon className="w-4 h-4" />
														</button>
														<button
															onClick={cancelEditingGroupName}
															className="inline-flex items-center justify-center w-6 h-6 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
														>
															<XIcon className="w-4 h-4" />
														</button>
													</div>
												) : (
													<div className="flex items-center gap-2">
														<h4 className="text-sm font-medium text-gray-900 m-0">{group.name}</h4>
														<button
															onClick={() => startEditingGroupName(group.id, group.name)}
															className="inline-flex items-center justify-center w-6 h-6 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
														>
															<EditIcon className="w-3 h-3" />
														</button>
													</div>
												)}
											</div>
											<div className="flex items-center gap-2">
												<button
													onClick={() => removeGroup(group.id)}
													className="inline-flex items-center gap-1 px-2 py-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
												>
													<TrashIcon className="w-4 h-4" />
												</button>
											</div>
										</div>

										<div className="space-y-2">
											{group.conditions.map((condition, conditionIndex) => (
												<div
													key={`${group.id}-condition-${conditionIndex}`}
													className="grid grid-cols-12 gap-2 items-center"
												>
													<div className="col-span-4">
														<select
															value={condition.field || ''}
															onChange={(e) =>
																updateCondition(group.id, condition.id, {
																	field: e.target.value,
																	value: null,
																})
															}
															className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
														>
															<option value="">Select field</option>
															{fields.map((field) => (
																<option key={field.internalKey} value={field.internalKey}>
																	{field.fieldName}
																</option>
															))}
														</select>
													</div>

													<div className="col-span-3">
														<select
															value={condition.operator || ''}
															onChange={(e) =>
																updateCondition(group.id, condition.id, {
																	operator: e.target.value as EFilterOperator,
																	value: null,
																})
															}
															disabled={!condition.field}
															className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
														>
															{getAvailableOperators(condition.field).map((operator) => (
																<option key={operator} value={operator}>
																	{FILTER_OPERATOR_LABELS[operator]}
																</option>
															))}
														</select>
													</div>

													<div className="col-span-4">
														{condition.field && (
															<DynamicField
																field={fields.find((f) => f.internalKey === condition.field)!}
																value={condition.value}
																mode={EFieldMode.FILTER}
																onChange={(value) => {
																	updateCondition(group.id, condition.id, { value });
																}}
															/>
														)}
													</div>

													<div className="col-span-1 flex justify-end">
														<button
															onClick={() => removeCondition(group.id, condition.id)}
															className="inline-flex items-center justify-center w-8 h-8 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
														>
															<TrashIcon className="w-4 h-4" />
														</button>
													</div>
												</div>
											))}
										</div>

										<button
											onClick={() => addCondition(group.id)}
											className="w-full mt-3 px-4 py-2 border-2 border-dashed border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:border-gray-400 hover:bg-white transition-colors"
										>
											<PlusIcon className="w-4 h-4 inline mr-2" />
											Add Condition
										</button>
									</div>
								))}
							</div>

							<div className="flex justify-start">
								<button
									onClick={addFilterGroup}
									className="inline-flex items-center gap-2 px-4 py-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
								>
									<PlusIcon className="w-4 h-4" />
									Add Filter Group
								</button>
							</div>
						</div>
					)}
				</div>
			</div>

			<Modal
				title="Save Filter as View"
				open={showSaveModal}
				onCancel={() => {
					setShowSaveModal(false);
					setViewName('');
				}}
				footer={[
					<AntButton
						key="cancel"
						onClick={() => {
							setShowSaveModal(false);
							setViewName('');
						}}
					>
						Cancel
					</AntButton>,
					<AntButton key="save" type="primary" onClick={handleNewViewSave} loading={isLoading}>
						Save & Apply
					</AntButton>,
				]}
			>
				<div className="space-y-4">
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-2">View Name</label>
						<Input
							placeholder="Enter a name for this filter view"
							value={viewName}
							onChange={(e) => setViewName(e.target.value)}
							onPressEnter={handleNewViewSave}
						/>
					</div>
					<div className="text-sm text-gray-600">
						This will save your current filter configuration as a reusable view that appears as a
						tab.
					</div>
				</div>
			</Modal>
		</>
	);
};
